import React, { useEffect } from 'react';
import { Card, Form, Select, Button, Tabs, Input, InputNumber, message, Divider } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';

const appLanguages = ['de', 'en', 'tr', 'ru', 'ar'];

const HomePageSettings: React.FC = () => {
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const { data: sliders } = useQuery({
        queryKey: ['adminSliders'],
        queryFn: async () => {
            const response = await apiClient.get('/admin/sliders');
            return response.data.data;
        }
    });

    const { data: config } = useQuery({
        queryKey: ['adminHomePageConfig'],
        queryFn: async () => {
            const response = await apiClient.get('/admin/homepage-config');
            return response.data.data;
        }
    });

    useEffect(() => {
        if (config) {
            const translations: Record<string, any> = {};
            appLanguages.forEach(lang => {
                translations[lang] = config.translations?.[lang] || {};
            });

            form.setFieldsValue({
                heroSliderId: config.heroSliderId,
                latestNewslettersCount: config.latestNewslettersCount || 5,
                translations
            });
        }
    }, [config, form]);

    const mutation = useMutation({
        mutationFn: async (values: any) => {
            const translations: Record<string, any> = {};
            appLanguages.forEach(lang => {
                translations[lang] = values.translations?.[lang] || {};
            });

            const payload = {
                heroSliderId: values.heroSliderId,
                latestNewslettersCount: values.latestNewslettersCount || 5,
                translations
            };
            const response = await apiClient.put('/admin/homepage-config', payload);
            return response.data;
        },
        onSuccess: () => {
            message.success('Homepage Einstellungen erfolgreich gespeichert');
            queryClient.invalidateQueries({ queryKey: ['adminHomePageConfig'] });
        },
        onError: () => {
            message.error('Fehler beim Speichern');
        }
    });

    const getLanguageLabel = (lang: string) => {
        if (lang === 'de') return 'Deutsch (Standard)';
        if (lang === 'tr') return 'Türkçe';
        if (lang === 'en') return 'English';
        return lang.toUpperCase();
    };

    const tabItems = appLanguages.map(lang => ({
        key: lang,
        label: getLanguageLabel(lang),
        children: (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-8 gap-y-6">
                
                {/* Fallback Hero Section */}
                <Card size="small" title="Hero Fallback (Wenn kein Slider)" className="bg-gray-50 xl:col-span-2 shadow-sm border-blue-100">
                    <Form.Item name={['translations', lang, 'heroFallbackTitle']} label="Titel (Fallback)">
                        <Input />
                    </Form.Item>
                    <Form.Item name={['translations', lang, 'heroFallbackSubtitle']} label="Untertitel (Fallback)">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <div className="flex gap-4">
                        <Form.Item name={['translations', lang, 'heroFallbackButton1Text']} label="Button 1 (Projekte)" className="flex-1">
                            <Input />
                        </Form.Item>
                        <Form.Item name={['translations', lang, 'heroFallbackButton2Text']} label="Button 2 (Kurse)" className="flex-1">
                            <Input />
                        </Form.Item>
                    </div>
                </Card>

                {/* Events Section */}
                <Card size="small" title="Events Block" className="bg-gray-50 shadow-sm">
                    <Form.Item name={['translations', lang, 'eventsTitle']} label="Titel">
                        <Input />
                    </Form.Item>
                    <Form.Item name={['translations', lang, 'eventsSubtitle']} label="Untertitel">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item name={['translations', lang, 'eventsViewAllText']} label="Text: 'Alle ansehen'">
                        <Input />
                    </Form.Item>
                    <Form.Item name={['translations', lang, 'eventsEmptyText']} label="Text: 'Keine Veranstaltungen'">
                        <Input />
                    </Form.Item>
                    <Form.Item name={['translations', lang, 'eventDetailsText']} label="Text: 'Details ansehen'">
                        <Input />
                    </Form.Item>
                </Card>

                {/* Newsletter Section */}
                <Card size="small" title="Newsletter Block" className="bg-gray-50 shadow-sm">
                    <Form.Item name={['translations', lang, 'newsletterTitle']} label="Titel">
                        <Input />
                    </Form.Item>
                    <Form.Item name={['translations', lang, 'newsletterSubtitle']} label="Untertitel">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Divider className="my-2" />
                    <Form.Item name={['translations', lang, 'newsletterButton1Text']} label="Button 1 Text">
                        <Input />
                    </Form.Item>
                    <Form.Item name={['translations', lang, 'newsletterButton1Url']} label="Button 1 URL">
                        <Input />
                    </Form.Item>
                    <Divider className="my-2" />
                    <Form.Item name={['translations', lang, 'newsletterButton2Text']} label="Button 2 Text">
                        <Input />
                    </Form.Item>
                    <Form.Item name={['translations', lang, 'newsletterButton2Url']} label="Button 2 URL">
                        <Input />
                    </Form.Item>
                </Card>

                {/* 4 Pillars Section */}
                <Card size="small" title="4 Säulen Block (Allgemein)" className="bg-gray-50 xl:col-span-2 shadow-sm border-orange-100">
                    <Form.Item name={['translations', lang, 'pillarsTitle']} label="Haupt-Titel">
                        <Input />
                    </Form.Item>
                    <Form.Item name={['translations', lang, 'pillarsSubtitle']} label="Haupt-Untertitel">
                        <Input.TextArea rows={2} />
                    </Form.Item>

                    <Divider />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="p-3 bg-white rounded border">
                            <strong>Säule 1 (Kinder & Jugend)</strong>
                            <Form.Item name={['translations', lang, 'pillar1Title']} label="Titel" className="mt-2 mb-2"><Input size="small" /></Form.Item>
                            <Form.Item name={['translations', lang, 'pillar1Desc']} label="Beschreibung" className="mb-0"><Input.TextArea rows={3} size="small" /></Form.Item>
                        </div>
                        <div className="p-3 bg-white rounded border">
                            <strong>Säule 2 (Erwachsenenbildung)</strong>
                            <Form.Item name={['translations', lang, 'pillar2Title']} label="Titel" className="mt-2 mb-2"><Input size="small" /></Form.Item>
                            <Form.Item name={['translations', lang, 'pillar2Desc']} label="Beschreibung" className="mb-0"><Input.TextArea rows={3} size="small" /></Form.Item>
                        </div>
                        <div className="p-3 bg-white rounded border">
                            <strong>Säule 3 (Gesellschaft)</strong>
                            <Form.Item name={['translations', lang, 'pillar3Title']} label="Titel" className="mt-2 mb-2"><Input size="small" /></Form.Item>
                            <Form.Item name={['translations', lang, 'pillar3Desc']} label="Beschreibung" className="mb-0"><Input.TextArea rows={3} size="small" /></Form.Item>
                        </div>
                        <div className="p-3 bg-white rounded border">
                            <strong>Säule 4 (Kultur & Kunst)</strong>
                            <Form.Item name={['translations', lang, 'pillar4Title']} label="Titel" className="mt-2 mb-2"><Input size="small" /></Form.Item>
                            <Form.Item name={['translations', lang, 'pillar4Desc']} label="Beschreibung" className="mb-0"><Input.TextArea rows={3} size="small" /></Form.Item>
                        </div>
                    </div>
                </Card>
            </div>
        )
    }));

    return (
        <Card title="Startseiten Einstellungen (Homepage Config)">
            <Form form={form} layout="vertical" onFinish={(values) => mutation.mutate(values)}>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 max-w-3xl bg-gray-50 p-4 rounded-xl border border-gray-200">
                    <Form.Item name="heroSliderId" label="Aktiver Hero Slider" className="mb-0">
                        <Select allowClear placeholder="Kein Slider (Verstecken, zeigt Fallback-Hero)">
                            {sliders?.map((s: any) => (
                                <Select.Option key={s.id} value={s.id}>{s.name} ({s.itemCount} Bilder)</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item 
                        name="latestNewslettersCount" 
                        label="Anzahl angezeigter Newsletter (Startseite & Buttons)" 
                        tooltip="Wie viele der neuesten Newsletter auf der Startseite und in der Seitenleiste angezeigt werden sollen (z.B. 5 oder 4)"
                        className="mb-0"
                    >
                        <InputNumber min={1} max={20} className="w-full" placeholder="5" />
                    </Form.Item>
                </div>

                <Tabs items={tabItems} type="card" />

                <div className="mt-8">
                    <Button type="primary" htmlType="submit" size="large" loading={mutation.isPending}>
                        Speichern
                    </Button>
                </div>
            </Form>
        </Card>
    );
};

export default HomePageSettings;
